import type { DuplicateAdIdentity } from "@shared/ads-manager-duplication";

export function currencyPrecision(currency: string | null): number | null {
  if (!currency || !/^[A-Z]{3}$/.test(currency)) return null;
  // Intl accepts arbitrary three-letter codes, so validate against its ISO list.
  if (!Intl.supportedValuesOf("currency").includes(currency)) return null;
  return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? null;
}

export function parseDailyBudget(text: string, currency: string | null): number | null {
  const precision = currencyPrecision(currency);
  if (precision === null || !/^\d+(?:\.\d+)?$/.test(text.trim())) return null;
  const [whole, fraction = ""] = text.trim().split(".");
  if (fraction.length > precision) return null;
  const minor = BigInt(whole) * 10n ** BigInt(precision) + BigInt(fraction.padEnd(precision, "0") || "0");
  return minor > 0n && minor <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(minor) : null;
}

export function formatMinorBudget(minor: number, currency: string): string {
  const precision = currencyPrecision(currency);
  if (precision === null || !Number.isSafeInteger(minor) || minor <= 0) return "";
  const digits = String(minor).padStart(precision + 1, "0");
  return precision ? `${digits.slice(0, -precision)}.${digits.slice(-precision)}` : digits;
}

export function sourceBudgetPrefill(ads: DuplicateAdIdentity[], currency: string | null): { value: string; reason: string | null } {
  if (!ads.length || currencyPrecision(currency) === null)
    return { value: "", reason: "Account currency is unavailable. A verified currency is required." };
  if (ads.some(ad => ad.source.budgetOwner !== "adset"))
    return { value: "", reason: "Campaign-controlled or unknown source budgets cannot prefill an ABO daily budget. Enter a budget for each new ad set." };
  if (ads.some(ad => ad.source.currency !== currency || !Number.isSafeInteger(ad.source.adsetDailyBudgetMinor) || (ad.source.adsetDailyBudgetMinor ?? 0) <= 0))
    return { value: "", reason: "A genuine source daily budget in the account currency is unavailable. Enter a daily budget." };
  const budgets = new Set(ads.map(ad => ad.source.adsetDailyBudgetMinor));
  if (budgets.size !== 1)
    return { value: "", reason: "Source daily budgets differ. Enter the daily budget to apply to each new ad set." };
  return { value: formatMinorBudget(ads[0].source.adsetDailyBudgetMinor!, currency!), reason: null };
}

export function duplicationErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : "Duplication is temporarily unavailable.";
  try {
    const parsed = JSON.parse(message.replace(/^\d{3}:\s*/, ""));
    if (typeof parsed.error === "string") return parsed.error;
  } catch { /* Network errors do not contain a JSON response. */ }
  return message;
}

export function duplicationSelectionEligibility(
  accountId: string,
  selectedIds: string[],
  rows: Array<{ id: string; level: string; accountId?: string }>,
  maxAds = 25,
) {
  const ads = selectedIds.map(id => rows.find(row => row.id === id));
  const adOnly = ads.length > 0 && ads.every(row => row?.level === "ad");
  const reason = selectedIds.length > maxAds ? `Select no more than ${maxAds} ads.`
    : !accountId || ads.some(row => row?.accountId && row.accountId.replace(/^act_/i, "") !== accountId.replace(/^act_/i, ""))
      ? "Select ads from a single account."
      : null;
  return { adOnly, reason };
}
